/**
 * ═══════════════════════════════════════════════════════════════════════════
 * MAILBOX SYNC MONITOR
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * Displays mailbox sync status and connection health
 * Focuses on sync quality rather than discrepancy tracking
 */

import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { 
  CheckCircle, 
  XCircle, 
  Activity,
  RefreshCw
} from 'lucide-react'
import { useMailboxTwoWaySync } from '@/hooks/useMailboxTwoWaySync'
import { formatDistanceToNow } from 'date-fns'

export const MailboxSyncMonitor = () => {
  const { 
    connections = [], 
    stats = {},
    performSync,
    isSyncing = false
  } = useMailboxTwoWaySync('smartlead')

  return (
    <div className="space-y-6">
      {/* Sync Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Connections</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold">{stats.totalConnections || 0}</div>
              <Activity className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-green-200 bg-green-50/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-green-700">Synced</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold text-green-600">{stats.syncedConnections || 0}</div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-yellow-200 bg-yellow-50/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-yellow-700">Conflicts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold text-yellow-600">{stats.conflictConnections || 0}</div>
              <RefreshCw className="h-8 w-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-red-200 bg-red-50/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-red-700">Failed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold text-red-600">{stats.failedConnections || 0}</div>
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Connection Health */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Connection Health</h3>
            <p className="text-sm text-muted-foreground">
              Real-time status of mailbox-to-Smartlead connections
            </p>
          </div>
          <Badge variant={isSyncing ? "default" : "secondary"}>
            {isSyncing ? "Syncing..." : "Idle"}
          </Badge>
        </div>

        <div className="space-y-2">
          {connections.slice(0, 10).map((conn: any) => (
            <Card key={conn.id} className="hover:bg-muted/50">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`h-2 w-2 rounded-full ${
                      conn.sync_status === 'synced' ? 'bg-green-500' : 
                      conn.sync_status === 'conflict' ? 'bg-yellow-500' : 
                      'bg-red-500'
                    }`} />
                    <div>
                      <p className="font-medium">{conn.mailboxes?.email || 'Unknown'}</p>
                      <p className="text-sm text-muted-foreground">
                        Last sync: {conn.last_synced_at ? formatDistanceToNow(new Date(conn.last_synced_at)) : 'Never'}
                      </p>
                    </div>
                  </div>
                  <Badge variant={conn.sync_status === 'synced' ? 'default' : 'destructive'}>
                    {conn.sync_status}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  )
}